import React, { useState, useEffect } from 'react';
import { useNavigation } from '../navigation';
import ExpityPurchasesWrapper from './expity-purchases-wrapper';
import SearchbarHomepage from './Searchbar-Homepage';
import QuickActions from '../../Quick-Actions';
import QuickPurchase from './Quick-Purchase';

const ENTITY_TYPE_API_URL =
  'https://pharma-amd-0-1-42.onrender.com/pharma/api/admin/entity-type';

const PharmaHomepage: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [showQuickPurchasePopup, setShowQuickPurchasePopup] = useState(false);
  const [clinicDisplayName, setClinicDisplayName] = useState<string>('');
  const [pharmaEntityType, setPharmaEntityType] = useState<string>('');

  // Logout handler - clears all localStorage and navigates to landing page
  const handleLogout = () => {
    // Clear all localStorage items
    localStorage.clear();
    // Navigate to landing page
    navigateTo('landing');
  };

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Trigger load animation
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // If session exists but entity type was never stored (e.g. hard refresh), fetch once
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || localStorage.getItem('pharmaEntityType')) return;
    let cancelled = false;
    (async () => {
      try {
        console.log('========================================');
        console.log('📋 ENTITY TYPE (homepage fallback) - API Call');
        console.log('========================================');
        console.log('Request URL:', ENTITY_TYPE_API_URL);
        console.log('Request method:', 'GET');
        console.log('Request headers:', {
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${token}`,
        });

        const res = await fetch(ENTITY_TYPE_API_URL, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'ngrok-skip-browser-warning': 'true',
            Authorization: `Bearer ${token}`,
          },
        });

        console.log('Entity type response status:', res.status);
        console.log('Entity type response headers:', Object.fromEntries(res.headers.entries()));

        if (cancelled) return;

        if (!res.ok) {
          const errText = await res.text();
          console.error('❌ Entity type fetch failed — status:', res.status);
          console.error('❌ Entity type error response body (raw):', errText);
          console.log('========================================');
          return;
        }

        const data = await res.json().catch(() => null);
        console.log('Entity type response body (parsed):', data);
        console.log('Entity type response body (JSON string):', JSON.stringify(data, null, 2));
        if (data && !cancelled) {
          localStorage.setItem('pharmaEntityType', JSON.stringify(data));
          console.log('✅ Entity type stored in localStorage key "pharmaEntityType"');
        }
        console.log('========================================');
      } catch (e) {
        console.error('❌ Entity type API error (homepage):', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load entity type from localStorage
  useEffect(() => {
    try {
      const entityTypeData = localStorage.getItem('pharmaEntityType');
      if (entityTypeData) {
        const parsed = JSON.parse(entityTypeData);
        const type = parsed?.entity_type || parsed?.type || '';
        setPharmaEntityType(type.toLowerCase());
      }
    } catch {
      // ignore
    }
  }, []);

  // Load clinic / pharmacy name from localStorage (full_name) for display above Quick Search
  useEffect(() => {
    try {
      const fullName = localStorage.getItem('full_name');
      if (fullName && fullName.trim()) {
        setClinicDisplayName(fullName.trim());
        return;
      }
      let name = '';
      const stored = localStorage.getItem('clinicDetails') || localStorage.getItem('pharmacyDetails');
      if (stored) {
        const parsed = JSON.parse(stored);
        name = parsed?.name || parsed?.clinic_name || parsed?.pharmacy_name || '';
      }
      if (!name) {
        const pharmaStr = localStorage.getItem('pharmaDetails');
        if (pharmaStr) {
          const pharma = JSON.parse(pharmaStr);
          name = pharma?.name || pharma?.clinic_name || pharma?.pharmacy_name || '';
        }
      }
      if (name) setClinicDisplayName(name);
    } catch {
      // ignore
    }
  }, []);

  // Sample medications data
  const defaultMedications = [
    {
      id: 1,
      name: 'Paracetamol 500mg',
      genericName: 'Acetaminophen',
      dosage: '500mg',
      form: 'Tablet',
      price: '$5.99',
      stock: 150,
      category: 'Pain Relief',
      description: 'Effective pain reliever and fever reducer',
      manufacturer: 'MediCorp',
      expiryDate: '2025-12-31'
    },
    {
      id: 2,
      name: 'Amoxicillin 250mg',
      genericName: 'Amoxicillin',
      dosage: '250mg',
      form: 'Capsule',
      price: '$12.50',
      stock: 75,
      category: 'Antibiotic',
      description: 'Broad-spectrum antibiotic for bacterial infections',
      manufacturer: 'PharmaPlus',
      expiryDate: '2025-08-15'
    },
    {
      id: 3,
      name: 'Omeprazole 20mg',
      genericName: 'Omeprazole',
      dosage: '20mg',
      form: 'Capsule',
      price: '$18.75',
      stock: 45,
      category: 'Gastrointestinal',
      description: 'Proton pump inhibitor for acid reflux treatment',
      manufacturer: 'HealthFirst',
      expiryDate: '2025-06-20'
    }
  ];

  const filteredMedications = defaultMedications.filter(medication =>
    medication.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medication.genericName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medication.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Pain Relief': return '#10b981';
      case 'Antibiotic': return '#ef4444';
      case 'Gastrointestinal': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getCategoryBgColor = (category: string) => {
    switch (category) {
      case 'Pain Relief': return '#ecfdf5';
      case 'Antibiotic': return '#fef2f2';
      case 'Gastrointestinal': return '#fffbeb';
      default: return '#f9fafb';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '0',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Enhanced Animated Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(245, 158, 11, 0.06) 0%, transparent 50%),
          radial-gradient(circle at 60% 30%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 10% 70%, rgba(236, 72, 153, 0.04) 0%, transparent 50%)
        `,
        animation: 'backgroundFloat 25s ease-in-out infinite'
      }}></div>

      {/* Floating Particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        {/* Particle 1 */}
        <div style={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          background: 'rgba(16, 185, 129, 0.3)',
          borderRadius: '50%',
          top: '20%',
          left: '10%',
          animation: 'particleFloat1 15s ease-in-out infinite'
        }}></div>
        
        {/* Particle 2 */}
        <div style={{
          position: 'absolute',
          width: '12px',
          height: '12px',
          background: 'rgba(59, 130, 246, 0.25)',
          borderRadius: '50%',
          top: '60%',
          left: '85%',
          animation: 'particleFloat2 18s ease-in-out infinite'
        }}></div>
        
        {/* Particle 3 */}
        <div style={{
          position: 'absolute',
          width: '6px',
          height: '6px',
          background: 'rgba(245, 158, 11, 0.4)',
          borderRadius: '50%',
          top: '40%',
          left: '70%',
          animation: 'particleFloat3 20s ease-in-out infinite'
        }}></div>
        
        {/* Particle 4 */}
        <div style={{
          position: 'absolute',
          width: '10px',
          height: '10px',
          background: 'rgba(139, 92, 246, 0.2)',
          borderRadius: '50%',
          top: '80%',
          left: '20%',
          animation: 'particleFloat4 16s ease-in-out infinite'
        }}></div>
        
        {/* Particle 5 */}
        <div style={{
          position: 'absolute',
          width: '14px',
          height: '14px',
          background: 'rgba(236, 72, 153, 0.15)',
          borderRadius: '50%',
          top: '10%',
          left: '60%',
          animation: 'particleFloat5 22s ease-in-out infinite'
        }}></div>
        
        {/* Particle 6 */}
        <div style={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          background: 'rgba(16, 185, 129, 0.2)',
          borderRadius: '50%',
          top: '70%',
          left: '50%',
          animation: 'particleFloat6 19s ease-in-out infinite'
        }}></div>
      </div>

      {/* Geometric Shapes */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        {/* Triangle 1 */}
        <div style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderLeft: '15px solid transparent',
          borderRight: '15px solid transparent',
          borderBottom: '25px solid rgba(16, 185, 129, 0.1)',
          top: '15%',
          left: '25%',
          animation: 'shapeRotate1 30s linear infinite'
        }}></div>
        
        {/* Triangle 2 */}
        <div style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderLeft: '20px solid transparent',
          borderRight: '20px solid transparent',
          borderBottom: '35px solid rgba(59, 130, 246, 0.08)',
          top: '65%',
          left: '75%',
          animation: 'shapeRotate2 35s linear infinite'
        }}></div>
        
        {/* Circle */}
        <div style={{
          position: 'absolute',
          width: '30px',
          height: '30px',
          border: '2px solid rgba(245, 158, 11, 0.15)',
          borderRadius: '50%',
          top: '35%',
          left: '15%',
          animation: 'shapePulse 12s ease-in-out infinite'
        }}></div>
        
        {/* Square */}
        <div style={{
          position: 'absolute',
          width: '25px',
          height: '25px',
          border: '2px solid rgba(139, 92, 246, 0.12)',
          borderRadius: '4px',
          top: '55%',
          left: '80%',
          animation: 'shapeRotate3 25s linear infinite'
        }}></div>
      </div>

      {/* Content Container */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* Enhanced Modern Header */}
        <header style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(25px)',
          borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
          padding: window.innerWidth < 768 ? '0.75rem 0' : '0.875rem 0',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          transform: isLoaded ? 'translateY(0)' : 'translateY(-20px)',
          opacity: isLoaded ? 1 : 0,
          transition: 'all 0.6s ease-out',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: window.innerWidth < 768 ? '0 1rem' : '0 2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: window.innerWidth < 768 ? 'wrap' : 'nowrap',
            gap: window.innerWidth < 768 ? '1rem' : '0'
          }}>
            {/* Enhanced Logo Section */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: window.innerWidth < 768 ? '0.75rem' : '1rem',
              flex: window.innerWidth < 768 ? '1' : 'none'
            }}>
              <div style={{
                width: window.innerWidth < 768 ? '36px' : '44px',
                height: window.innerWidth < 768 ? '36px' : '44px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: window.innerWidth < 768 ? '10px' : '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Shine effect */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                  animation: 'shine 3s ease-in-out infinite'
                }}></div>
                <svg width={window.innerWidth < 768 ? "18" : "24"} height={window.innerWidth < 768 ? "18" : "24"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z"/>
                  <path d="M2 17L12 22L22 17"/>
                  <path d="M2 12L12 17L22 12"/>
                </svg>
              </div>
              <div>
                <h1 style={{
                  fontSize: window.innerWidth < 768 ? '1.1rem' : '1.5rem',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #1f2937 0%, #10b981 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0,
                  letterSpacing: '-0.03em',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  lineHeight: '1.2'
                }}>
                  Sperow - Pharma
                </h1>
                <p style={{
                  fontSize: window.innerWidth < 768 ? '0.7rem' : '0.8rem',
                  color: '#6b7280',
                  margin: 0,
                  fontWeight: '600',
                  letterSpacing: '0.02em',
                  display: window.innerWidth < 480 ? 'none' : 'block',
                  lineHeight: '1.2'
                }}>
                  Professional Management Dashboard
                </p>
              </div>
            </div>

            {/* Enhanced Time and Actions */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: window.innerWidth < 768 ? '0.75rem' : '1.25rem',
              flexWrap: window.innerWidth < 768 ? 'wrap' : 'nowrap',
              width: window.innerWidth < 768 ? '100%' : 'auto',
              justifyContent: window.innerWidth < 768 ? 'space-between' : 'flex-end'
            }}>
              {/* Enhanced Live Clock */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                padding: window.innerWidth < 768 ? '0.625rem 0.875rem' : '0.75rem 1rem',
                borderRadius: '10px',
                border: '1px solid rgba(16, 185, 129, 0.15)',
                textAlign: 'center',
                minWidth: window.innerWidth < 768 ? '110px' : '130px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                overflow: 'hidden',
                display: window.innerWidth < 480 ? 'none' : 'block'
              }}>
                {/* Subtle gradient overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.02) 0%, transparent 100%)',
                  pointerEvents: 'none'
                }}></div>
                <div style={{
                  fontSize: window.innerWidth < 768 ? '0.65rem' : '0.75rem',
                  color: '#6b7280',
                  fontWeight: '600',
                  marginBottom: '0.2rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  lineHeight: '1.2'
                }}>
                  {currentTime.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
                <div style={{
                  fontSize: window.innerWidth < 768 ? '0.75rem' : '0.85rem',
                  color: '#1f2937',
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  letterSpacing: '0.05em',
                  lineHeight: '1.2'
                }}>
                  {currentTime.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit'
                  })}
                </div>
              </div>

              {/* Notification Icon - hidden for independent pharmacies */}
              {pharmaEntityType !== 'independent' && (
              <button
                type="button"
                aria-label="Notifications"
                title="Notifications"
                onClick={() => navigateTo('notifications')}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                  border: '1px solid rgba(5, 150, 105, 0.9)',
                  padding: window.innerWidth < 768 ? '0.625rem 0.75rem' : '0.75rem 0.875rem',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  minWidth: window.innerWidth < 768 ? '40px' : '44px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#047857';
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(5, 150, 105, 0.9)';
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.25)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg width={window.innerWidth < 768 ? "16" : "18"} height={window.innerWidth < 768 ? "16" : "18"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 17h5l-1.4-1.4a2 2 0 0 1-.6-1.4V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
                  <path d="M10 20a2 2 0 0 0 4 0" />
                </svg>
              </button>
              )}

              {/* Enhanced Back Button */}
              <button
                onClick={handleLogout}
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  color: '#6b7280',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  padding: window.innerWidth < 768 ? '0.625rem 0.875rem' : '0.75rem 1rem',
                  borderRadius: '10px',
                  fontSize: window.innerWidth < 768 ? '0.75rem' : '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: window.innerWidth < 768 ? '0.5rem' : '0.5rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.color = '#10b981';
                  e.currentTarget.style.backgroundColor = '#f0fdf4';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                  e.currentTarget.style.color = '#6b7280';
                  e.currentTarget.style.backgroundColor = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg width={window.innerWidth < 768 ? "14" : "16"} height={window.innerWidth < 768 ? "14" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16,17 21,12 16,7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                {window.innerWidth < 480 ? 'Back' : 'Logout'}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: window.innerWidth < 768 ? '2rem 1rem' : '3rem 2rem',
          transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
          opacity: isLoaded ? 1 : 0,
          transition: 'all 0.6s ease-out 0.2s'
        }}>
          {/* Hero Section */}
          <section style={{
            marginBottom: window.innerWidth < 768 ? '2rem' : '4rem',
            display: 'flex',
            alignItems: showQuickPurchasePopup ? 'center' : 'flex-start',
            justifyContent: showQuickPurchasePopup ? 'center' : 'flex-start',
            gap: window.innerWidth < 768 ? '2rem' : '3rem',
            flexDirection: showQuickPurchasePopup ? 'column' : (window.innerWidth < 1024 ? 'column' : 'row'),
            minHeight: showQuickPurchasePopup ? (window.innerWidth < 768 ? 'calc(100vh - 200px)' : 'calc(100vh - 250px)') : 'auto'
          }}>
            {/* Quick Actions Component */}
            {!showQuickPurchasePopup && (
              <QuickActions 
                showQuickPurchasePopup={showQuickPurchasePopup}
                onShowQuickPurchasePopup={setShowQuickPurchasePopup}
              />
            )}

            {/* Quick Purchase Component */}
            {showQuickPurchasePopup && (
              <div style={{ 
                width: '100%',
                maxWidth: '100%',
                margin: '0 auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <QuickPurchase onClose={() => setShowQuickPurchasePopup(false)} />
              </div>
            )}

            {/* Right Side Content */}
            {!showQuickPurchasePopup && (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: window.innerWidth < 768 ? '2rem' : '3rem',
              width: window.innerWidth < 1024 ? '100%' : 'auto'
            }}>
              {/* Clinic Name - displayed above Quick Search (same gradient as Sperow - Pharma header) */}
              {clinicDisplayName && (
                <div style={{
                  textAlign: 'center',
                  padding: window.innerWidth < 768 ? '0.75rem 0' : '1rem 0'
                }}>
                  <div style={{
                    display: 'inline-block',
                    background: 'rgba(255, 254, 254, 0.22)',
                    padding: window.innerWidth < 768 ? '0.5rem 1rem' : '1rem 4rem',
                    borderRadius: '12px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(0, 0, 0, 0.06)'
                  }}>
                    <span style={{
                      fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
                      fontSize: window.innerWidth < 768 ? '1.5rem' : '2rem',
                      fontWeight: '800',
                      background: 'linear-gradient(135deg, #1f2937 0%, #10b981 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      letterSpacing: '-0.03em'
                    }}>
                      {clinicDisplayName}
                    </span>
                  </div>
                </div>
              )}

              {/* Quick Search - moved below clinic name */}
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: window.innerWidth < 768 ? '0.75rem' : '1rem',
                    marginBottom: window.innerWidth < 768 ? '1.5rem' : '2rem',
                    flexWrap: window.innerWidth < 480 ? 'wrap' : 'nowrap'
                  }}
                >
                  <div
                    style={{
                      width: window.innerWidth < 768 ? '40px' : '50px',
                      height: window.innerWidth < 768 ? '40px' : '50px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: window.innerWidth < 768 ? '10px' : '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    <svg
                      width={window.innerWidth < 768 ? '20' : '24'}
                      height={window.innerWidth < 768 ? '20' : '24'}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                  </div>
                  <h2
                    style={{
                      fontSize: window.innerWidth < 768 ? '1.1rem' : '1.25rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: 0
                    }}
                  >
                    Quick Search
                  </h2>
                </div>

                <SearchbarHomepage value={searchTerm} onChange={setSearchTerm} />
              </div>

              {/* Quick Actions Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth < 768 ? '1fr' : window.innerWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: window.innerWidth < 768 ? '1.5rem' : '2.5rem'
              }}>
              {/* Purchase Card */}
              <div
                onClick={() => navigateTo('purchase-page')}
                style={{
                  background: 'white',
                  borderRadius: window.innerWidth < 768 ? '16px' : '20px',
                  padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 1 ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: hoveredCard === 1 ? '0 12px 24px rgba(16, 185, 129, 0.15)' : '0 4px 6px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: window.innerWidth < 768 ? '120px' : '140px'
                }}
                onMouseEnter={() => setHoveredCard(1)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={{
                  width: window.innerWidth < 768 ? '50px' : '60px',
                  height: window.innerWidth < 768 ? '50px' : '60px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: window.innerWidth < 768 ? '10px' : '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  marginBottom: window.innerWidth < 768 ? '0.5rem' : '0.75rem',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}>
                  <svg width={window.innerWidth < 768 ? "24" : "28"} height={window.innerWidth < 768 ? "24" : "28"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6"/>
                  </svg>
                </div>
                <h3 style={{
                  fontSize: window.innerWidth < 768 ? '1.1rem' : '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0,
                  textAlign: 'center'
                }}>
                  Purchase
                </h3>
              </div>

              {/* Sell/Bill Card */}
              <div
                onClick={() => navigateTo('billing-page')}
                style={{
                  background: 'white',
                  borderRadius: window.innerWidth < 768 ? '16px' : '20px',
                  padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 2 ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: hoveredCard === 2 ? '0 12px 24px rgba(239, 68, 68, 0.15)' : '0 4px 6px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: window.innerWidth < 768 ? '120px' : '140px'
                }}
                onMouseEnter={() => setHoveredCard(2)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={{
                  width: window.innerWidth < 768 ? '50px' : '60px',
                  height: window.innerWidth < 768 ? '50px' : '60px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  borderRadius: window.innerWidth < 768 ? '10px' : '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  marginBottom: window.innerWidth < 768 ? '0.5rem' : '0.75rem',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}>
                  <svg width={window.innerWidth < 768 ? "24" : "28"} height={window.innerWidth < 768 ? "24" : "28"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
                <h3 style={{
                  fontSize: window.innerWidth < 768 ? '1.1rem' : '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0,
                  textAlign: 'center'
                }}>
                  Sell/Bill
                </h3>
              </div>

              {/* Inventory Card */}
              <div
                onClick={() => navigateTo('inventory')}
                style={{
                  background: 'white',
                  borderRadius: window.innerWidth < 768 ? '16px' : '20px',
                  padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 3 ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: hoveredCard === 3 ? '0 12px 24px rgba(245, 158, 11, 0.15)' : '0 4px 6px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: window.innerWidth < 768 ? '120px' : '140px'
                }}
                onMouseEnter={() => setHoveredCard(3)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={{
                  width: window.innerWidth < 768 ? '50px' : '60px',
                  height: window.innerWidth < 768 ? '50px' : '60px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: window.innerWidth < 768 ? '10px' : '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  marginBottom: window.innerWidth < 768 ? '0.5rem' : '0.75rem',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                }}>
                  <svg width={window.innerWidth < 768 ? "24" : "28"} height={window.innerWidth < 768 ? "24" : "28"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                  </svg>
                </div>
                <h3 style={{
                  fontSize: window.innerWidth < 768 ? '1.1rem' : '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0,
                  textAlign: 'center'
                }}>
                  Inventory
                </h3>
              </div>
            </div>
            </div>
            )}

          {/* Expiry Alerts / Recent Purchases Wrapper */}
          {!showQuickPurchasePopup && <ExpityPurchasesWrapper />}
          </section>

        </main>
      </div>

      <style>{`
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-webkit-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-moz-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input:-ms-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes backgroundFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes particleFloat1 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.3;
          }
          25% {
            transform: translate(20px, -30px) scale(1.2);
            opacity: 0.6;
          }
          50% {
            transform: translate(-15px, -20px) scale(0.8);
            opacity: 0.4;
          }
          75% {
            transform: translate(10px, 25px) scale(1.1);
            opacity: 0.5;
          }
        }

        @keyframes particleFloat2 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.25;
          }
          33% {
            transform: translate(-25px, 20px) scale(1.3);
            opacity: 0.5;
          }
          66% {
            transform: translate(30px, -15px) scale(0.7);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat3 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.4;
          }
          20% {
            transform: translate(15px, -25px) scale(1.4);
            opacity: 0.7;
          }
          40% {
            transform: translate(-20px, 10px) scale(0.6);
            opacity: 0.2;
          }
          60% {
            transform: translate(25px, 20px) scale(1.1);
            opacity: 0.5;
          }
          80% {
            transform: translate(-10px, -15px) scale(0.9);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat4 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.2;
          }
          30% {
            transform: translate(-30px, -20px) scale(1.5);
            opacity: 0.6;
          }
          60% {
            transform: translate(20px, 30px) scale(0.5);
            opacity: 0.1;
          }
        }

        @keyframes particleFloat5 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.15;
          }
          25% {
            transform: translate(35px, -25px) scale(1.6);
            opacity: 0.4;
          }
          50% {
            transform: translate(-25px, 35px) scale(0.4);
            opacity: 0.05;
          }
          75% {
            transform: translate(15px, -10px) scale(1.2);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat6 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.2;
          }
          40% {
            transform: translate(-20px, -30px) scale(1.3);
            opacity: 0.5;
          }
          80% {
            transform: translate(30px, 20px) scale(0.7);
            opacity: 0.1;
          }
        }

        @keyframes shapeRotate1 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: rotate(180deg) scale(1.2);
          }
          100% {
            transform: rotate(360deg) scale(1);
          }
        }

        @keyframes shapeRotate2 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: rotate(-180deg) scale(0.8);
          }
          100% {
            transform: rotate(-360deg) scale(1);
          }
        }

        @keyframes shapeRotate3 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          100% {
            transform: rotate(360deg) scale(1);
          }
        }

        @keyframes shapePulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.3;
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          50% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

      `}</style>
    </div>
  );
};

export default PharmaHomepage;